import React, { useEffect } from 'react'
import { PropTypes } from 'prop-types'
import ReactGA from 'react-ga'

const sendPageView = location => {
  const { pathname, search, hash } = location
  const page = pathname + search + hash
  ReactGA.set({ page })
  ReactGA.pageview(page)
}

export const ReactRouterHistoryTracker = ({ history, children }) => {
  useEffect(() => {
    sendPageView(history.location)
    history.listen(sendPageView)
  }, [history])
  return children
}

export const useLoadTimer = ({ category, label, variable = 'load' }) => {
  useEffect(() => {
    // TODO: track if a browser doesn't have performance.now?
    if (!window.performance || !window.performance.now) {
      return
    }
    const start = performance.now()
    return () => {
      // Timing data must be an integer value
      // https://developers.google.com/analytics/devguides/collection/analyticsjs/field-reference#timingValue
      const value = Math.round(performance.now() - start)
      ReactGA.timing({ variable, category, label, value })
    }
  }, [ category, label, variable ])
}

export const useEventOnMount = ({action, category, label, value}) => {
  useEffect(() => {
    getEventSender({action, category, label, value})()
  }, [ action, category, label, value ])
}

export const useEventOnUnmount = ({ action, category, label, value }) => {
  useEffect(() => () => {
    getEventSender({ action, category, label, value })()
  }, [ action, category, label, value ])
}

const nop = () => {}
const getEventSender = eventData => {
  if (typeof eventData === 'function') {
    return arg => getEventSender(eventData(arg))()
  }
  if (Array.isArray(eventData)) {
    if (typeof eventData[0] === 'string') {
      return () => ReactGA.plugin.execute(...eventData)
    }
    return () => eventData.map(ed => getEventSender(ed)())
  }
  if (eventData) {
    return () => ReactGA.event(eventData)
  }
  return nop
}

export const withTracking = arg => {
  let eventMap, eventProps
  const wrapper = component => ({ ...props }) => {
    eventProps.forEach(prop => {
      const eventData = props[prop]
      delete props[prop]
      props[eventMap[prop]] = getEventSender(eventData)
    })
    return component(props)
  }
  if (typeof arg === 'function') {
    eventMap = { eventData: 'sendEvent' }
    eventProps = ['eventData']
    return wrapper(arg)
  }
  eventMap = arg
  eventProps = Object.keys(eventMap)
  return wrapper
}

export const TrackEvent = ({ wrapper = 'span', eventName = 'onClick', eventData, children }) => {
  const sendEvent = getEventSender(eventData)
  if (typeof children === 'function') {
    return children(sendEvent)
  }
  return React.createElement(wrapper, { [eventName]: sendEvent }, children)
}

// This is possibly overkill

// ReactGA.event()
// https://github.com/react-ga/react-ga#reactgaeventargs
const eventType = PropTypes.shape({
  category: PropTypes.string.isRequired,
  action: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.number,
  nonInteraction: PropTypes.bool,
  transport: PropTypes.oneOf(['beacon', 'xhr', 'image'])
})

// ReactGA.plugin.execute()
// https://github.com/react-ga/react-ga#reactgapluginexecutepluginname-action-actiontype-modificationErrors
const pluginType = (props, propName, componentName) => {
  const arr = props[propName]
  const getError = msg => new Error(`Invalid prop "${propName}" supplied to "${componentName}". ${msg}`)
  if (arr.length < 2 || arr.length > 4) {
    return getError('Expected an array with length between 2 and 4.')
  }
  if (typeof arr[0] !== 'string') {
    return getError('Plugin name (array item 0) must be a string')
  }
  if (typeof arr[1] !== 'string') {
    return getError('Action (array item 1) must be a string')
  }
}

TrackEvent.propTypes = {
  wrapper: PropTypes.string,
  eventName: PropTypes.string,
  eventData: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([eventType, pluginType])),
    PropTypes.func,
    eventType,
    pluginType
  ]).isRequired,
  children: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.node
  ]).isRequired
}
